import { useEffect, useCallback } from 'react';
import { useHistoryStore, HistoryAction } from '@/stores/historyStore';
import { useCanvasStore, type Item } from '@/stores/canvasStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export function useUndoRedo() {
  const { undo, redo, canUndo, canRedo } = useHistoryStore();
  const { 
    items, 
    currentBoardId,
    fetchItems,
  } = useCanvasStore();

  const executeUndo = useCallback(async () => {
    const action = undo();
    if (!action) return;

    try {
      switch (action.type) {
        case 'CREATE_ITEM':
          // Delete the created item
          if (action.items && action.items.length > 0) {
            await supabase.from('items').delete().in('id', action.items.map(i => i.id));
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Undo: Item deleted');
          }
          break;

        case 'DELETE_ITEM':
        case 'DELETE_ITEMS':
          // Recreate the deleted items
          if (action.items && action.items.length > 0) {
            for (const item of action.items) {
              await supabase.from('items').insert({
                id: item.id,
                board_id: item.board_id,
                item_type: item.item_type,
                position_x: item.position_x,
                position_y: item.position_y,
                width: item.width,
                height: item.height,
                z_index: item.z_index,
                content: item.content as Json,
                user_id: item.user_id,
              });
            }
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success(`Undo: ${action.items.length} item(s) restored`);
          }
          break;

        case 'MOVE_ITEM':
        case 'MOVE_ITEMS':
          // Restore previous positions
          if (action.previousPositions) {
            for (const [itemId, pos] of action.previousPositions) {
              await supabase.from('items').update({
                position_x: pos.x,
                position_y: pos.y,
              }).eq('id', itemId);
            }
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Undo: Position restored');
          }
          break;

        case 'UPDATE_CONTENT':
          // Restore previous content
          if (action.itemId && action.previousContent) {
            await supabase.from('items').update({
              content: action.previousContent as Json,
            }).eq('id', action.itemId);
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Undo: Content restored');
          }
          break;

        case 'MOVE_TO_BOARD':
          // Move items back to previous board
          if (action.itemIds && action.previousBoardId) {
            await supabase.from('items').update({
              board_id: action.previousBoardId,
            }).in('id', action.itemIds);
            
            // Also restore positions if available
            if (action.previousPositions) {
              for (const [itemId, pos] of action.previousPositions) {
                await supabase.from('items').update({
                  position_x: pos.x,
                  position_y: pos.y,
                }).eq('id', itemId);
              }
            }
            
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Undo: Items moved back');
          }
          break;
      }
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error('Undo failed');
    }
  }, [undo, currentBoardId, fetchItems]);

  const executeRedo = useCallback(async () => {
    const action = redo();
    if (!action) return;

    try {
      switch (action.type) {
        case 'CREATE_ITEM':
          // Recreate the item
          if (action.items && action.items.length > 0) {
            for (const item of action.items) {
              await supabase.from('items').insert({
                id: item.id,
                board_id: item.board_id,
                item_type: item.item_type,
                position_x: item.position_x,
                position_y: item.position_y,
                width: item.width,
                height: item.height,
                z_index: item.z_index,
                content: item.content as Json,
                user_id: item.user_id,
              });
            }
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Redo: Item created');
          }
          break;

        case 'DELETE_ITEM':
        case 'DELETE_ITEMS':
          // Delete the items again
          if (action.items && action.items.length > 0) {
            await supabase.from('items').delete().in('id', action.items.map(i => i.id));
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success(`Redo: ${action.items.length} item(s) deleted`);
          }
          break;

        case 'MOVE_ITEM':
        case 'MOVE_ITEMS':
          // Apply new positions
          if (action.newPositions) {
            for (const [itemId, pos] of action.newPositions) {
              await supabase.from('items').update({
                position_x: pos.x,
                position_y: pos.y,
              }).eq('id', itemId);
            }
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Redo: Position applied');
          }
          break;

        case 'UPDATE_CONTENT':
          // Apply new content
          if (action.itemId && action.newContent) {
            await supabase.from('items').update({
              content: action.newContent as Json,
            }).eq('id', action.itemId);
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Redo: Content applied');
          }
          break;

        case 'MOVE_TO_BOARD':
          // Move items to new board again
          if (action.itemIds && action.newBoardId) {
            await supabase.from('items').update({
              board_id: action.newBoardId,
            }).in('id', action.itemIds);
            if (currentBoardId) await fetchItems(currentBoardId);
            toast.success('Redo: Items moved');
          }
          break;
      }
    } catch (error) {
      console.error('Redo failed:', error);
      toast.error('Redo failed');
    }
  }, [redo, currentBoardId, fetchItems]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if typing in input
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName.toLowerCase();
        if (tagName === 'input' || tagName === 'textarea') return;
        if (activeElement.getAttribute('contenteditable') === 'true') return;
        if (activeElement.closest('.partnr-chat-window')) return;
      }

      // Ctrl/Cmd + Z = Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        executeUndo();
        return;
      }

      // Ctrl/Cmd + Shift + Z = Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        executeRedo();
        return;
      }

      // Ctrl/Cmd + Y = Redo (alternative)
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        executeRedo();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [executeUndo, executeRedo]);

  return {
    executeUndo,
    executeRedo,
    canUndo: canUndo(),
    canRedo: canRedo(),
  };
}
